/**
 * UserDashboardPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Statistic,
  ModalLayout,
  ScrollLayout,
  Text,
} from 'components';
import {
  Button,
  DatePicker,
  Input,
  Radio,
  Select,
  Tag,
  InputNumber,
  Descriptions as AntDescriptions,
  Divider,
  Spin,
  Table,
  Flex,
  Empty,
  Dropdown,
  Space,
  Image,
  Tooltip,
  Modal,
} from 'antd';
import {
  EditOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
  DownOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import Formatter from 'utils/Formatter';
import MessageAlert, {
  calculateAge,
  getDateRange,
  STORE_URL,
  stringToMoneyFormat,
} from 'utils';
import moment from 'moment';
import { decrypt } from 'utils/Crypto';
import {
  CouponUserApi,
  JmPayApi,
  OrderApi,
  PointHistoryApi,
  ShopOrderApi,
  StampHistoryApi,
  ParcelServiceApi,
  RentalApi,
} from 'api';
import { downloadXlsx } from 'utils/XlsxDownloader';
import { UserRegister } from '..';
import { OrderHistory } from './components';
import { openAddressModal } from 'utils/DaumAPIManager';
import dayjs from 'dayjs';

/**
 * TODO JM 피그마 요청 사항
 * --
 * 1. 삭제 시 회원의 미완료 주문 여부, 적립금 여부를 확인하며 미완료 주문이 있는 경우 삭제되지 않습니다.
 * 2. [자세히보기]를 체크하면 각 회원의 적립금, 예치금, 접속, 주문, 총 주문금액, 총 실결제금액을 확인할 수 있습니다.
 * 3. 해당 기능은 100개 이하 목록에서는 바로 확인할 수 있으며, 200개 이상은 [관련 내역 보기]의 각 버튼을 통해 확인할 수 있습니다.
 * 4. 휴면회원의 개인정보는 분리, 보관되어 쇼핑몰 관리자의 '고객관리 > 회원관리 > 휴면회원 관리'에서 별도로 조회할 수 있으며, 휴면회원이 로그인시 자동으로 휴면해제가 됩니다.
 * 5. 일반전화 또는 스마트폰 아이콘을 클릭하면 전화상담메모 창을 띄웁니다.
 */

const SIGNUP_PATH = {
  MEMBER: { label: '일반가입', color: 'processing' },
  NAVER: { label: '네이버', color: 'var(--naver-color)' },
  GOOGLE: { label: '구글', color: '#F2F2F2' },
  APPLE: { label: '애플', color: '#000000' },
  KAKAO: { label: '카카오', color: 'var(--kakao-color)' },
  NONE: { label: '비회원', color: 'default' },
};

const ACCOUNT_STATUS = {
  ACTIVE: { label: '정상', color: 'green' },
  INACTIVE: { label: '비활성', color: 'orange' },
  DORMANT: { label: '휴면', color: 'grey' },
  WITHDRAWN: { label: '탈퇴', color: 'red' },
};

const USER_SETTING = {
  location_consent: '위치정보 서비스 이용약관 동의',
  marketing_consent: '마케팅정보 수집 및 활용 동의',
  push_notification: '푸시알림',
  event_notification: '프로모션/이벤트 알림',
  terms_of_use: '이용약관 동의 여부',
  terms_of_privacy: '개인정보 수집 및 이용 동의 여부',
  terms_of_marketing: '마케팅 목적의 개인정보 수집 및 이용 동의 여부',
  terms_of_ad: '혜택 및 광고성 정보 수신 동의',
  terms_of_age: '만 14세 이하',
};

const JM_PAY = {
  MANUAL_CHARGE: {
    label: '수동충전',
    color: 'success',
  },
  AUTO_CHARGE: {
    label: '자동충전',
    color: 'success',
  },
  OVER_CHARGE: {
    label: '초과충전',
    color: 'success',
  },
  USE: {
    label: '페이사용',
    color: 'processing',
  },
  REFUNDED: {
    label: '사용환불',
    color: 'warning',
  },
  CANCELLED: {
    label: '충전취소',
    color: 'error',
  },
};

const PERIOD_ITEMS = [
  { label: '오늘', value: '1-day' },
  { label: '3일 이내', value: '3-day' },
  { label: '7일 이내', value: '7-day' },
  { label: '1개월 이내', value: '1-month' },
  { label: '3개월 이내', value: '3-month' },
  { label: '6개월 이내', value: '6-month' },
  { label: '1년 이내', value: '1-year' },
  { label: '2년 이내', value: '2-year' },
];

const FILTER_PLATFORM = [
  { label: '전체', value: 'all' },
  { label: '오더', value: 'order' },
  { label: '쇼핑몰', value: 'shop_order' },
];
const COUPON_TYPE = {
  MENU: { label: '메뉴 할인', color: 'default' },
  MD: { label: 'MD 할인', color: 'default' },
  DESSERT: { label: '디저트 할인', color: 'default' },
  ALL: { label: '전체 할인', color: 'default' },
  PRODUCT: { label: '상품 할인', color: 'default' },
  FREQUENCY: { label: '프리퀀시', color: 'default' },
};

const VOUCHER_TYPE = {
  EXCHANGE: { label: '교환권', color: 'default' },
  PERCENT_DISCOUNT: { label: '금액할인', color: 'default' },
  AMOUNT_DISCOUNT: { label: '%할인', color: 'default' },
};

const UserDashboardPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    modal,
    users,
    grades,
    page,
    count,
    expand,
    onUpdateQuery,
    onChange,
    searchInputValue,
    onSubmit,
    onSearch,
    parameter,
    navigate,
    onUsedCoupon,
    location,
    onReservePoint,
    selected,
    onSelect,
    isLoading,
    setIsLoading,
    onBulk,
    onUpdateGrade,
    onApprove,
    period,
    setPeriod,
    setPage,
    selecetedPlatform,
    setSelecetedPlatform,

    coupon,
    onIssueCoupon,
    onDetailModal,
    onXlsxDownload,
    onDeleteUser,
    refetch,
    MessageAlert,
  } = props;

  /* ===== STATE ===== */
  const [visible, setVisible] = React.useState(false);

  const [isOpened, setIsOpened] = React.useState(false);
  const [modalType, setModalType] = React.useState('');
  const [memo, setMemo] = React.useState('');
  const [modalData, setModalData] = React.useState(null);

  const [detailTab, setDetailTab] = React.useState('default');
  const [jmpayModal, setJmpayModal] = React.useState(null);
  const [updateModal, setUpdateModal] = React.useState(null);

  const [isCreate, setIsCreate] = React.useState(false);

  const [inputValue, setInputValue] = React.useState({});
  const [inputReserveValue, setInputReserveValue] = React.useState({});

  const [inputGrade, setInputGrade] = React.useState({
    grade_id: null,
    grade_reason: '',
    grade_name: '',
  });

  // 쿠폰 발급
  const [isIssueModal, setIsIssueModal] = React.useState(false);
  const [selectedCoupon, setSelectedCoupon] = React.useState(null);
  const [searchArea, setSearchArea] = React.useState(''); // 검색
  const [issuedNumb, setIssuedNumb] = React.useState(1);

  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 150,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },

    couponLayout: {
      width: '100%',
      backgroundColor: '#f4f4f4',
      borderLeft: '20px solid #ddd',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 20,
      paddingLeft: 20,
      position: 'relative',
      top: 0,
      left: 0,
    },
    couponTitle: {
      fontSize: 18,
      fontWeight: 700,
      marginBottom: 16,
    },
    couponStatus: {
      color: 'var(--black-color)',
      fontSize: 16,
      lineHeight: 1.75,
    },
    couponDate: {
      fontSize: 14,
      fontWeight: 700,
      color: 'var(--dark-gray-color)',
    },

    labelStyle: {
      // width: 155,
      width: '18%',
      color: '#111',
      fontWeight: '500',
      background: '#f4f4f4',
    },
    contentStyle: { width: '32%' },
    defaultStyle: {
      borderTop: '2px solid #d1d1d1',
      borderRadius: 5,
      marginTop: 5,
    },
  };

  /**
   * 등급 Select Item
   * --
   */
  const selectGradeList = grades
    ?.slice()
    ?.sort((a, b) => a?.grade_id - b?.grade_id)
    ?.map((grade) => ({
      label: grade?.grade_name,
      value: grade?.grade_name,
    }));

  const formItem = [
    {
      label: '회원구분',
      form: [
        {
          input: (
            <Radio.Group value={searchInputValue?.isMember ?? null}>
              <Radio
                value={null}
                onChange={(e) => onChange('isMember', e.target.value)}
              >
                전체
              </Radio>
              <Radio
                value={'Y'}
                onChange={(e) => onChange('isMember', e.target.value)}
              >
                회원
              </Radio>
              <Radio
                value={'N'}
                onChange={(e) => onChange('isMember', e.target.value)}
              >
                비회원
              </Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '특별관리여부',
      form: [
        {
          input: (
            <Radio.Group value={searchInputValue?.isCaution ?? null}>
              <Radio
                value={null}
                onChange={(e) => onChange('isCaution', e.target.value)}
              >
                전체
              </Radio>
              <Radio
                value={'O'}
                onChange={(e) => onChange('isCaution', e.target.value)}
              >
                주문회원
              </Radio>
              <Radio
                value={'N'}
                onChange={(e) => onChange('isCaution', e.target.value)}
              >
                비주문회원
              </Radio>
              <Radio
                value={'Y'}
                onChange={(e) => onChange('isCaution', e.target.value)}
              >
                특별관리회원
              </Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '사업자구분',
      form: [
        {
          input: (
            <Radio.Group value={searchInputValue?.isBusiness ?? null}>
              <Radio
                value={null}
                onChange={(e) => onChange('isBusiness', e.target.value)}
              >
                전체
              </Radio>
              <Radio
                value={'NONE'}
                onChange={(e) => onChange('isBusiness', e.target.value)}
              >
                일반 회원
              </Radio>
              <Radio
                value={'APPLY'}
                onChange={(e) => onChange('isBusiness', e.target.value)}
              >
                사업자 신청 회원
              </Radio>
              <Radio
                value={'APPROVE'}
                onChange={(e) => onChange('isBusiness', e.target.value)}
              >
                사업자 회원
              </Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    {
      label: '검색어',
      form: [
        {
          input: (
            <Select
              value={searchInputValue?.searchKey}
              options={[
                { label: '아이디', value: 'account' },
                { label: '회원명', value: 'name' },
                { label: '전화번호', value: 'phone' },
                { label: '주소', value: 'area' },
              ]}
              style={{ ...styles.form.input.large }}
              onChange={(e) => onChange('searchKey', e)}
            />
          ),
        },
        {
          input: (
            <Input
              placeholder="검색어를 입력해주세요"
              style={{ ...styles.form.input.large }}
              value={searchInputValue?.searchValue}
              onChange={(e) => onChange('searchValue', e.target.value)}
            />
          ),
        },
      ],
    },
    {
      label: '회원등급/상태',
      form: [
        {
          input: (
            <>
              <Select
                value={searchInputValue?.grade ?? null}
                options={
                  selectGradeList
                    ? [{ label: '전체', value: null }, ...selectGradeList]
                    : [{ label: '전체', value: null }]
                }
                style={{ ...styles.form.input.large }}
                onChange={(e) => onChange('grade', e)}
              />
            </>
          ),
        },
        {
          input: (
            <Radio.Group
              value={searchInputValue?.status ?? ''}
              onChange={(e) => onChange('status', e.target.value)}
              style={{ marginLeft: 10 }}
              options={[
                { label: '전체', value: '' },
                { label: '활성화', value: 'ACTIVE' },
                { label: '비활성화', value: 'INACTIVE' },
                { label: '휴면', value: 'DORMANT' },
                { label: '탈퇴', value: 'WITHDRAWN' },
              ]}
            />
          ),
        },
      ],
    },
    {
      label: '회원가입일',
      form: [
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(0);

                onChange('joinStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('joinEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              오늘
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(7);

                onChange('joinStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('joinEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              1주일
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(30);

                onChange('joinStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('joinEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              1개월
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(90);

                onChange('joinStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('joinEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              3개월
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                onChange('joinStartDate', '');
                onChange('joinEndDate', '');
              }}
            >
              전체
            </Button>
          ),
        },
        {
          input: (
            <>
              <DatePicker
                format={'YYYY-MM-DD'}
                placeholder=""
                style={{ ...styles.form.date }}
                {...(searchInputValue?.joinStartDate
                  ? {
                      value: searchInputValue?.joinStartDate
                        ? moment(searchInputValue?.joinStartDate)
                        : '',
                    }
                  : {})}
                onChange={(date, dateString) =>
                  onChange('joinStartDate', dateString)
                }
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <DatePicker
                format={'YYYY-MM-DD'}
                placeholder=""
                style={{ ...styles.form.date }}
                {...(searchInputValue?.joinEndDate
                  ? {
                      value: searchInputValue?.joinEndDate
                        ? moment(searchInputValue?.joinEndDate)
                        : '',
                    }
                  : {})}
                onChange={(date, dateString) =>
                  onChange('joinEndDate', dateString)
                }
              />
            </>
          ),
        },
      ],
    },
    {
      label: '나이',
      form: [
        {
          input: (
            <>
              <InputNumber
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large, marginRight: 0 }}
                value={searchInputValue?.ageStart}
                onChange={(e) => onChange('ageStart', e)}
                maxLength={2}
                min={0}
                max={searchInputValue?.ageEnd ?? 99}
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <InputNumber
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large }}
                value={searchInputValue?.ageEnd}
                onChange={(e) => onChange('ageEnd', e)}
                maxLength={2}
                min={searchInputValue?.ageStart ?? 0}
              />
              <p style={{ ...styles.form.text }}>세</p>
            </>
          ),
        },
      ],
    },
    {
      label: '생년월일',
      form: [
        {
          input: (
            <>
              <InputNumber
                placeholder="월 단위로 입력"
                style={{ ...styles.form.input.large, marginRight: 0 }}
                value={searchInputValue?.birthdayStart}
                onChange={(e) => onChange('birthdayStart', e)}
                maxLength={2}
                min={0}
                max={searchInputValue?.birthdayEnd ?? 12}
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <InputNumber
                placeholder="월 단위로 입력"
                style={{ ...styles.form.input.large }}
                value={searchInputValue?.birthdayEnd}
                onChange={(e) => onChange('birthdayEnd', e)}
                maxLength={2}
                max={12}
                min={searchInputValue?.birthdayStart ?? 0}
              />
              <p style={{ ...styles.form.text }}>월</p>
            </>
          ),
        },
      ],
    },
    {
      label: '성별',
      form: [
        {
          input: (
            <Radio.Group value={searchInputValue?.gender ?? null}>
              <Radio
                value={null}
                onChange={(e) => onChange('gender', e.target.value)}
              >
                전체
              </Radio>
              <Radio
                value={'남'}
                onChange={(e) => onChange('gender', e.target.value)}
              >
                남
              </Radio>
              <Radio
                value={'여'}
                onChange={(e) => onChange('gender', e.target.value)}
              >
                여
              </Radio>
            </Radio.Group>
          ),
        },
      ],
    },
    // SECTION: 최근 구매 건수
    {
      label: (
        <>
          최근 구매건수{' '}
          <Tooltip
            title={
              <span>취소/환불된 주문을 제외한 주문건들을 의미합니다.</span>
            }
          >
            <span style={{ paddingLeft: 5, color: 'var(--light-gray-color)' }}>
              <QuestionCircleOutlined />
            </span>
          </Tooltip>
        </>
      ),
      form: [
        {
          input: (
            <Select
              placeholder="주문플랫폼"
              options={[
                { label: '전체', value: 'totalCount' },
                { label: '쇼핑몰', value: 'totalShopOrderCount' },
                { label: '오더', value: 'totalOrderCount' },
              ]}
              style={{ ...styles.form.input.large }}
              value={searchInputValue?.countPlatform ?? null}
              onChange={(e) => {
                if (selecetedPlatform !== 'all')
                  return MessageAlert.info(
                    '확인할 플렛폼을 전체로 바꿔주세요.',
                  );
                onChange('countPlatform', e);
              }}
            />
          ),
        },
        {
          input: (
            <Flex gap={4} align="center">
              <InputNumber
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large, marginRight: 0 }}
                value={searchInputValue?.countStart}
                onChange={(e) => onChange('countStart', e)}
                max={searchInputValue?.countEnd ?? 999999}
              />
              <p>~</p>
              <InputNumber
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large, marginRight: 0 }}
                value={searchInputValue?.countEnd}
                onChange={(e) => onChange('countEnd', e)}
                min={searchInputValue?.countStart ?? 0}
              />
              <p style={{ ...styles.form.text }}>건</p>
            </Flex>
          ),
        },
      ],
    },
    // SECTION: 최근 구매 금액
    {
      label: (
        <>
          최근 구매금액{' '}
          <Tooltip
            title={
              <span>취소/환불된 주문을 제외한 주문건들을 의미합니다.</span>
            }
          >
            <span style={{ paddingLeft: 5, color: 'var(--light-gray-color)' }}>
              <QuestionCircleOutlined />
            </span>
          </Tooltip>
        </>
      ),
      form: [
        {
          input: (
            <Select
              placeholder="주문플랫폼"
              options={[
                { label: '전체', value: 'totalPrice' },
                { label: '쇼핑몰', value: 'totalShopOrderPrice' },
                { label: '오더', value: 'totalOrderPrice' },
              ]}
              style={{ ...styles.form.input.large }}
              value={searchInputValue?.pricePlatform ?? null}
              onChange={(e) => {
                if (selecetedPlatform !== 'all')
                  return MessageAlert.info(
                    '확인할 플렛폼을 전체로 바꿔주세요.',
                  );
                onChange('pricePlatform', e);
              }}
            />
          ),
        },
        {
          input: (
            <Flex gap={4} align="center">
              <InputNumber
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large, marginRight: 0 }}
                value={searchInputValue?.priceStart}
                onChange={(e) => onChange('priceStart', e)}
                max={searchInputValue?.priceEnd ?? Infinity}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
              <p>~</p>
              <InputNumber
                placeholder="숫자만 입력"
                style={{ ...styles.form.input.large, marginRight: 0 }}
                value={searchInputValue?.priceEnd}
                onChange={(e) => onChange('priceEnd', e)}
                min={searchInputValue?.priceStart ?? 0}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
              <p style={{ ...styles.form.text }}>원</p>
            </Flex>
          ),
        },
      ],
    },

    // SECTION: 주문 상품
    // {
    //   label: '주문상품',
    //   form: [
    //     {
    //       input: (
    //         <Select
    //
    //           value={'상품번호'}
    //           options={[
    //             { label: '상품번호', value: '상품번호' },
    //             { label: '상품명', value: '상품명' },
    //           ]}
    //           style={{ ...styles.form.input.large }}
    //         />
    //       ),
    //     },
    //     {
    //       input: (
    //         <Input
    //
    //           placeholder="검색어를 입력해주세요"
    //           style={{ ...styles.form.input.large }}
    //         />
    //       ),
    //     },
    //   ],
    // },
    {
      label: '접속일',
      form: [
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(0);

                onChange('accessStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('accessEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              오늘
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(7);

                onChange('accessStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('accessEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              1주일
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(30);

                onChange('accessStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('accessEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              1개월
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                const [start, end] = getDateRange(90);

                onChange('accessStartDate', moment(start).format('YYYY-MM-DD'));
                onChange('accessEndDate', moment(end).format('YYYY-MM-DD'));
              }}
            >
              3개월
            </Button>
          ),
        },
        {
          input: (
            <Button
              style={{ ...styles.form.button }}
              onClick={() => {
                onChange('accessStartDate', '');
                onChange('accessEndDate', '');
              }}
            >
              전체
            </Button>
          ),
        },
        {
          input: (
            <>
              <DatePicker
                format={'YYYY-MM-DD'}
                placeholder=""
                style={{ ...styles.form.date }}
                {...(searchInputValue?.accessStartDate
                  ? {
                      value: searchInputValue?.accessStartDate
                        ? moment(searchInputValue?.accessStartDate)
                        : '',
                    }
                  : {})}
                onChange={(date, dateString) =>
                  onChange('accessStartDate', dateString)
                }
              />
              <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
              <DatePicker
                format={'YYYY-MM-DD'}
                placeholder=""
                style={{ ...styles.form.date }}
                {...(searchInputValue?.accessEndDate
                  ? {
                      value: searchInputValue?.accessEndDate
                        ? moment(searchInputValue?.accessEndDate)
                        : '',
                    }
                  : {})}
                onChange={(date, dateString) =>
                  onChange('accessEndDate', dateString)
                }
              />
            </>
          ),
        },
      ],
    },
    {
      label: '구매 건수/구매 금액 검색 기간',
      form: [
        {
          input: PERIOD_ITEMS.map((item) => {
            return (
              <Button
                style={
                  item.value === period
                    ? {
                        ...styles.form.button,
                        color: 'blue',
                        borderColor: 'blue',
                      }
                    : { ...styles.form.button }
                }
                onClick={() => {
                  setPeriod(item.value);
                }}
              >
                {item.label}
              </Button>
            );
          }),
        },
      ],
    },
    {
      label: '확인할 플랫폼',
      form: [
        {
          input: FILTER_PLATFORM.map((item) => {
            return (
              <Button
                style={
                  item.value === selecetedPlatform
                    ? {
                        ...styles.form.button,
                        color: 'blue',
                        borderColor: 'blue',
                      }
                    : { ...styles.form.button }
                }
                onClick={() => {
                  setSelecetedPlatform(item.value);
                }}
              >
                {item.label}
              </Button>
            );
          }),
        },
      ],
    },
  ];

  const col = [
    {
      title: '이름',
      key: 'name',
      dataIndex: 'name',
      fixed: 'left',
      sorter: (a, b) => a?.name?.localeCompare(b?.name, 'ko'),
      render: (item, record) => (
        <Flex gap={4}>
          {item}
          {record?.isCaution === 'Y' ? <Tag color="error">특별관리</Tag> : null}
          {record?.lastAccess >= 7 &&
          record?.status === 'ACTIVE' &&
          item !== '-' ? (
            <Tag>휴면예정</Tag>
          ) : null}
          {record?.isBusiness === 'APPROVE' ? (
            <Tag color="#1677FF">사업자</Tag>
          ) : null}
        </Flex>
      ),
    },
    {
      title: '아이디',
      key: 'account',
      dataIndex: 'account',
      fixed: 'left',
    },
    {
      title: '연락처',
      key: 'phone',
      dataIndex: 'phone',
      fixed: 'left',
    },
    {
      title: '이메일',
      key: 'email',
      dataIndex: 'email',
    },
    {
      title: '등급',
      key: 'grade',
      dataIndex: 'grade',
    },
    {
      title: '성별',
      key: 'gender',
      dataIndex: 'gender',
    },
    {
      title: '나이',
      key: 'age',
      dataIndex: 'age',
      sorter: (a, b) => a?.age - b?.age,
    },
    {
      title: '지역',
      key: 'area',
      dataIndex: 'area',
      width: 300,
      filterDropdown: ({ selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            value={searchArea}
            onChange={(e) => setSearchArea(e.target.value)}
            onPressEnter={() => confirm()}
            placeholder={`지역 검색`}
          />
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              검색
            </Button>
            <Button
              onClick={() => {
                setSearchArea(''); // 초기화 시 검색 텍스트 비우기
                clearFilters();
              }}
              size="small"
              style={{ width: 90 }}
            >
              초기화
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: '회원가입일',
      key: 'created_at',
      dataIndex: 'created_at',
      render: (item) => Formatter.unixToDate(item, 'YYYY-MM-DD'),
      sorter: (a, b) => a?.created_at - b?.created_at,
    },
    {
      title: '가입경로',
      key: 'path',
      dataIndex: 'path',
      render: (item) => (
        <Tag color={SIGNUP_PATH[item]?.color}>{SIGNUP_PATH[item]?.label}</Tag>
      ),
      filters: [
        { text: '일반가입', value: 'MEMBER' },
        { text: '네이버', value: 'NAVER' },
        { text: '구글', value: 'GOOGLE' },
        { text: '애플', value: 'APPLE' },
        { text: '카카오', value: 'KAKAO' },
        { text: '비회원', value: 'NONE' },
      ],
      onFilter: (value, record) => {
        return record?.path?.includes(value);
      },
    },
    {
      title: '계정상태',
      key: 'status',
      dataIndex: 'status',
      render: (item) => (
        <Tag color={ACCOUNT_STATUS[item]?.color}>
          {ACCOUNT_STATUS[item]?.label}
        </Tag>
      ),
    },
    {
      title: '최근접속일',
      key: 'accessTime',
      dataIndex: 'accessTime',
      render: (item) => item,
      sorter: (a, b) => a?.accessTime - b?.accessTime,
    },

    {
      title: `총 ${
        PERIOD_ITEMS?.find((item) => item?.value === period)?.label
      } 구매 건수`,
      key: 'totalCount',
      // dataIndex: 'totalCount',
      render: (_, record) => {
        if (selecetedPlatform === 'shop_order') {
          return stringToMoneyFormat(record.totalShopOrderCount, '건');
        }
        if (selecetedPlatform === 'order') {
          return stringToMoneyFormat(record.totalOrderCount, '건');
        }
        return stringToMoneyFormat(record.totalCount, '건');
      },
      sorter: (a, b) => a?.totalCount - b?.totalCount,
    },
    {
      title: `총 ${
        PERIOD_ITEMS?.find((item) => item?.value === period)?.label
      } 구매 금액`,
      key: 'totalPrice',
      // dataIndex: 'totalPrice',
      render: (_, record) => {
        if (selecetedPlatform === 'shop_order') {
          return stringToMoneyFormat(record.totalShopOrderPrice, '원');
        }
        if (selecetedPlatform === 'order') {
          return stringToMoneyFormat(record.totalOrderPrice, '원');
        }
        return stringToMoneyFormat(record.totalPrice, '원');
      },
      sorter: (a, b) => a?.totalPrice - b?.totalPrice,
    },
    {
      title: `쇼핑몰 ${
        PERIOD_ITEMS?.find((item) => item?.value === period)?.label
      } 구매 건수`,
      key: 'totalShopOrderCount',
      dataIndex: 'totalShopOrderCount',
      render: (item) =>
        selecetedPlatform === 'order' ? '-' : stringToMoneyFormat(item, '건'),
      sorter: (a, b) => a?.totalShopOrderCount - b?.totalShopOrderCount,
    },
    {
      title: `쇼핑몰 ${
        PERIOD_ITEMS?.find((item) => item?.value === period)?.label
      } 구매 금액`,
      key: 'totalShopOrderPrice',
      dataIndex: 'totalShopOrderPrice',
      render: (item) =>
        selecetedPlatform === 'order' ? '-' : stringToMoneyFormat(item, '원'),
      sorter: (a, b) => a?.totalShopOrderPrice - b?.totalShopOrderPrice,
    },
    {
      title: `오더 ${
        PERIOD_ITEMS?.find((item) => item?.value === period)?.label
      } 구매 건수`,
      key: 'totalOrderCount',
      dataIndex: 'totalOrderCount',
      render: (item) =>
        selecetedPlatform === 'shop_order'
          ? '-'
          : stringToMoneyFormat(item, '건'),
      sorter: (a, b) => a?.totalOrderCount - b?.totalOrderCount,
    },
    {
      title: `오더 ${
        PERIOD_ITEMS?.find((item) => item?.value === period)?.label
      } 구매 금액`,
      key: 'totalOrderPrice',
      dataIndex: 'totalOrderPrice',
      render: (item) =>
        selecetedPlatform === 'shop_order'
          ? '-'
          : stringToMoneyFormat(item, '원'),
      sorter: (a, b) => a?.totalOrderPrice - b?.totalOrderPrice,
    },
  ];

  const [searchText, setSearchText] = React.useState('');
  const [searchedColumn, setSearchedColumn] = React.useState('');
  const searchInput = React.useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });

  /* ===== FUNCTION ===== */
  /**
   * 오늘 날짜 unix 타입 반환
   * --
   * @returns {array}
   */
  const getTodayUnixTimes = () => {
    const now = new Date();

    // 오늘 00:00:00
    const startOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfDayUnix = Math.floor(startOfDay.getTime() / 1000);

    // 오늘 23:59:59
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
    );
    const endOfDayUnix = Math.floor(endOfDay.getTime() / 1000);

    return [startOfDayUnix, endOfDayUnix];
  };

  /**
   * 모달 최신 데이터 반영
   * --
   * @param {object} data
   */
  const fetchModalData = (data) => {
    setModalData((prev) => ({
      ...prev,
      ...data,
    }));
    setIsOpened(!isOpened);
    setModalType('');
    setUpdateModal(null);
  };

  /**
   * 회원 상세 데이터
   * --
   */
  const getModalData = React.useCallback(async (id, mData) => {
    try {
      const { data: orderData } = await OrderApi.getOrders({
        user_id: id,
      });
      const { data: couponData } = await CouponUserApi.getCouponUsers({
        user_id: id,
      });
      const { data: pointData } = await PointHistoryApi.getPointHistories({
        user_id: id,
      });
      const { data: shopOrderData } = await ShopOrderApi.getShopOrders({
        user_id: id,
        join_tables: '["parcel_service","rental"]',
      });
      const { data: JmPayData } = await JmPayApi.getJmPays({
        user_id: id,
      });
      const { data: stampData } = await StampHistoryApi.getStampHistories({
        user_id: id,
        expired_date: false,
        use_date: false,
      });
      const parcelResult = await ParcelServiceApi.getParcelServices({
        join_tables: '["refund"]',
      });
      const rentalRefsult = await RentalApi.getRentals({
        join_tables: '["refund"]',
      });

      const nowDateUnix = dayjs().unix();

      setModalData({
        ...mData,
        orderHistory:
          orderData?.slice()?.sort((a, b) => b?.created_at - a?.created_at) ??
          [],
        totalOrderPrice: orderData?.reduce((acc, cur) => {
          if (cur?.order_status === 'COMPLETED') {
            acc += cur?.final_price || 0;
          }
          return acc;
        }, 0),
        couponStorage: couponData
          ? couponData.reduce(
              (acc, cur) => {
                const { use_date, expired_date } = cur;

                if (
                  !use_date &&
                  (expired_date > Formatter.dateToUnix() || !expired_date)
                ) {
                  acc['useable'].push(cur);
                } else {
                  acc['used'].push(cur);
                }
                return acc;
              },
              { used: [], useable: [] },
            )
          : { used: [], useable: [] },
        pointHistory: pointData,
        totalPoint: pointData?.reduce((acc, cur) => {
          const { expired_date, point, use_point, type } = cur;

          if (type !== 'EXPIRED' && nowDateUnix < expired_date) {
            acc += point - use_point;
          }
          return acc;
        }, 0),
        shopOrderHistory:
          shopOrderData
            ?.slice()
            ?.sort((a, b) => b?.created_at - a?.created_at) ?? [],
        totalShopOrderPice: shopOrderData?.reduce((acc, cur) => {
          if (cur?.payment_state === 'PARTIALLY_REFUNDED') {
            if (cur?.type === 'RENTAL') {
              const rentalRefundPrice = cur?.rental?.reduce((r, d) => {
                const filterRefundData = rentalRefsult?.data?.map((i) => {
                  let rentalResult = {
                    refund_price: 0,
                    rental_id: i?.rental_id,
                  };
                  if (i.rental_id === d.rental_id) {
                    rentalResult = {
                      refund_price: i.refund?.reduce((p, q) => {
                        return (p += q.refund_amount);
                      }, 0),
                      rental_id: i.rental_id,
                    };
                  }
                  return rentalResult;
                });
                const rentalResult = filterRefundData.reduce((re, vl) => {
                  return (re += vl.refund_price);
                }, 0);
                return (r += rentalResult);
              }, 0);
              return (acc += cur?.final_price - rentalRefundPrice);
            } else {
              const refundPrice = cur?.parcel_service?.reduce((r, d) => {
                const filterRefundData = parcelResult?.data?.map((i) => {
                  let result = {
                    refund_price: 0,
                    parcel_service_id: i.parcel_service_id,
                  };
                  if (i.parcel_service_id === d.parcel_service_id) {
                    result = {
                      refund_price: i.refund?.reduce((p, q) => {
                        return (p += q.refund_amount);
                      }, 0),
                      parcel_service_id: i.parcel_service_id,
                    };
                  }
                  return result;
                });
                const result = filterRefundData.reduce((re, vl) => {
                  return (re += vl.refund_price);
                }, 0);
                return (r += result);
              }, 0);
              return (acc += cur?.final_price - refundPrice);
            }
          }
          return (acc += cur?.final_price);
        }, 0),
        jmpay: JmPayData?.slice()?.sort(
          (a, b) => b?.created_at - a?.created_at,
        ),
        stampHistory: stampData,
      });
    } catch (err) {
      console.error('[getModalData] Error!! ', err);
      throw err;
    }
  }, []);

  /**
   * 회원 정보 변경
   * --
   * @param {string} v
   * @returns
   */
  const changeAccountStatus = (t, v) => {
    const confirm = (msg = '') => window.confirm(msg);

    switch (t) {
      case 'account_status':
        if (!confirm('회원 계정상태를 변경하시겠습니까?')) return;
        onSubmit(t, modalData?.user_id, { [t]: v }, fetchModalData);
        break;
      case 'grade_id':
        if (!confirm('회원 등급을 변경하시겠습니까?')) return;
        onSubmit(
          t,
          v,
          {
            user_ids: [{ user_id: Number(decrypt(decodeURIComponent(modal))) }],
          },
          fetchModalData,
        );
        break;
      default:
        return;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 회원 테이블 데이터 정제
   * --
   */
  const tableData = React.useMemo(() => {
    const newUsers = users
      ?.map((user) => {
        const lastAccessTime = dayjs(user?.access_time * 1000);

        return {
          key: user?.user_id,
          userId: user?.user_id,
          created_at: user?.created_at,
          join: user?.created_at,
          name: user?.user_name ?? '-',
          phone: user?.user_phone,
          email: user?.user_email ?? '-',
          account: user?.user_account ?? '-',
          grade: user?.grade?.grade_name ?? '-',
          gender: user?.user_gender === 'M' ? '남' : '여',
          age: user?.user_birthday
            ? calculateAge(Formatter.unixToDate(user?.user_birthday))?.age
            : '-',
          area: `${user?.user_address ?? '-'}`,
          path: user?.user_name
            ? user?.social_type
              ? user?.social_type
              : 'MEMBER'
            : 'NONE',
          status: user?.account_status,
          access: user?.access_time,
          isMember:
            user?.user_account || (user?.social_type && user?.social_id)
              ? 'Y'
              : 'N',
          birthday: user?.user_birthday,
          isCaution: user?.is_caution,
          isBusiness:
            user?.business_level === 0
              ? 'NONE'
              : user?.business_level === 1
                ? 'APPLY'
                : 'APPROVE',
          accessTime: user?.access_time
            ? Formatter.unixToDate(user?.access_time, 'YYYY-MM-DD')
            : '-',
          lastAccess: dayjs().diff(lastAccessTime, 'month'),

          totalCount:
            Number(user?.total_order_count) +
            Number(user?.total_shop_order_count),
          totalPrice:
            Number(user?.total_order_price) +
            Number(
              user?.total_shop_order_price - user?.partial_refund_amount ?? 0,
            ),
          totalShopOrderCount: Number(user?.total_shop_order_count),
          totalShopOrderPrice: Number(
            user?.total_shop_order_price - user?.partial_refund_amount ?? 0,
          ),
          totalOrderCount: Number(user?.total_order_count),
          totalOrderPrice: Number(user?.total_order_price),
        };
      })
      ?.sort((a, b) => a?.created_at - b?.created_at);

    // 검색어별 필터처리
    const {
      searchKey,
      searchValue,
      count: c,
      page: p,
      expand: ex,
      modal: m,
      ...remainParameter
    } = parameter;

    const filterUsers = newUsers?.filter((user) =>
      Object.entries(remainParameter).every(([key, value]) => {
        if (!value || key.includes('Platform')) return true;

        // 키값이 시작값인 경우
        if (key.includes('Start')) {
          const newKey = key.split('Start');

          if (key.includes('Date')) {
            const unix = Formatter.dateToUnix(`${value} 00:00:00`);

            return user[newKey[0]] >= unix;
          }

          if (key.includes('birthday')) {
            if (!user[newKey[0]]) return false;
            return Number(Formatter.unixToDate(user[newKey[0]], 'MM')) >= value;
          }

          if (key.includes('count')) {
            return user[remainParameter?.countPlatform] >= Number(value);
          }

          if (key.includes('price')) {
            return user[remainParameter?.pricePlatform] >= Number(value);
          }

          return user[newKey[0]] <= value;
        }

        // 키값이 종료값인 경우
        if (key.includes('End')) {
          const newKey = key.split('End');

          if (key.includes('Date')) {
            const unix = Formatter.dateToUnix(`${value} 23:59:59`);
            return user[newKey[0]] <= unix;
          }

          if (key.includes('birthday')) {
            if (!user[newKey[0]]) return false;
            return Number(Formatter.unixToDate(user[newKey[0]], 'MM')) <= value;
          }

          if (key.includes('count')) {
            return user[remainParameter?.countPlatform] <= Number(value);
          }

          if (key.includes('price')) {
            return user[remainParameter?.pricePlatform] <= Number(value);
          }

          return user[newKey[0]] >= value;
        }

        if (key.includes('isCaution')) {
          if (value === 'Y') {
            return user[key] === value;
          } else if (value === 'O') {
            return user['totalCount'] > 0;
          } else if (value === 'N') {
            return user['totalCount'] === 0;
          } else {
            return true;
          }
        }

        return user[key] === value;
      }),
    );

    let searchUsers =
      searchKey && searchValue
        ? filterUsers?.filter((user) =>
            searchKey === 'phone'
              ? user[searchKey]
                  ?.replace(/-/g, '')
                  ?.includes(searchValue.replace(/-/g, ''))
              : user[searchKey]?.includes(searchValue),
          )
        : filterUsers;

    if (searchArea) {
      searchUsers = filterUsers?.filter((user) =>
        user?.area?.includes(searchArea),
      );
    }

    return {
      data: searchUsers,
      total: searchUsers?.length,
      total_data: searchUsers,
    };
  }, [users, parameter, searchArea]);

  /**
   * 회원 요약 데이터 정제
   * --
   */
  const summaryUserData = React.useMemo(() => {
    // NOTE: 오늘 날짜 시작일 종료일 [YYYY-MM-DD 00:00:00 ~ YYYY-MM-DD 23:59:59]
    const [startTime, endTime] = getTodayUnixTimes();

    let summary = {
      NEW: {
        title: '신규 회원',
        value: 0,
      },
      VISIT: {
        title: '방문회원(로그인한회원)',
        value: 0,
      },
      INACTIVE: {
        title: '휴면 회원',
        value: 0,
      },
      WITHDRAWN: {
        title: '탈퇴 회원',
        value: 0,
      },
      APPLY_BUSINESS: {
        title: '사업자 신청',
        value: 0,
      },
      APPROVE_BUSINESS: {
        title: '사업자 회원',
        value: 0,
      },
      TOTAL: {
        title: '전체 회원',
        value: 0,
      },
    };

    users?.forEach(
      ({ account_status, access_time, created_at, business_level }) => {
        const isNewMember = created_at > startTime && created_at < endTime;
        const isVisitMember = access_time > startTime && access_time < endTime;

        // 오늘 신규회원
        if (isNewMember) {
          ++summary['NEW']['value'];
        }

        // 오늘 방문(로그인) 회원
        if (isVisitMember) {
          ++summary['VISIT']['value'];
        }

        // 상태별 사용자 집계
        if (account_status === 'INACTIVE' || account_status === 'WITHDRAWN') {
          ++summary[account_status]['value'];
        }

        // 사업자 집계
        if (business_level) {
          if (business_level === 1) {
            ++summary['APPLY_BUSINESS']['value'];
          } else {
            ++summary['APPROVE_BUSINESS']['value'];
          }
        }

        // 전체 회원
        ++summary['TOTAL']['value'];
      },
    );

    return Object.entries(summary).map(([key, value]) => ({
      key,
      ...value,
    }));
  }, [users]);

  /**
   * 모달 데이터
   * --
   */
  React.useEffect(() => {
    if (modal) {
      const userId = decrypt(decodeURIComponent(modal));

      const findData = users?.find((user) => user?.user_id === Number(userId));

      getModalData(Number(userId), findData);
      setMemo(findData?.memo);
    } else {
      setModalData(null);
      setMemo(null);
    }
    // eslint-disable-next-line
  }, [modal, users]);
  /**
   * 플랫폼에 따라서 구매금액과 금액건수를 자동 책정
   * 2026.1.22 -willy
   */
  React.useEffect(() => {
    switch (selecetedPlatform) {
      case 'all':
        onChange('countPlatform', 'totalCount');
        onChange('pricePlatform', 'totalPrice');
        return;
      case 'shop_order':
        onChange('countPlatform', 'totalShopOrderCount');
        onChange('pricePlatform', 'totalShopOrderPrice');
        return;
      case 'order':
        onChange('countPlatform', 'totalOrderCount');
        onChange('pricePlatform', 'totalOrderPrice');
        return;
    }
  }, [selecetedPlatform]);

  /* ===== RENDER ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={24}>
              <Title style={{ ...styles.title }}>대시보드</Title>

              {/* 요약 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 40, display: 'flex' }}
              >
                <Flex gap={4} flex={1} align="center">
                  {summaryUserData?.map((_item, _index) => (
                    <div
                      key={`${_item.label}-${_index}`}
                      style={{
                        background: '#FFFFFF',
                        borderRadius: 4,
                        width: `calc(100% / 6)`,
                        padding: 20,
                      }}
                    >
                      <Statistic
                        title={_item.title}
                        value={_item.value}
                        suffix={'명'}
                        direction={'row'}
                        style={{ justifyContent: 'center' }}
                      />
                    </div>
                  ))}
                </Flex>
              </Content>
              {/* 요약 끝 */}

              {/* 검색필터 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 40 }}
              >
                <ExpandableContainer
                  borderRadius={4}
                  padding={20}
                  maxRows={5}
                  isExpand={expand === 'Y' ? true : false}
                  setIsExpand={() =>
                    onUpdateQuery('expand', expand === 'Y' ? 'N' : 'Y')
                  }
                >
                  {formItem.map((_item, _index) => (
                    <div
                      key={`form_${_index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 0px',
                      }}
                    >
                      <div
                        style={{ width: 120, fontSize: 13, fontWeight: 400 }}
                      >
                        <label>{_item.label}</label>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {_item.form.map((_subitem, _subkey) => (
                          <React.Fragment key={`input_${_subkey}`}>
                            {_subitem.input}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </ExpandableContainer>

                {/* 검색/초기화 버튼 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 48,
                  }}
                >
                  <Button
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => onSearch()}
                  >
                    검색
                  </Button>
                  <Button
                    style={{ ...styles.button }}
                    onClick={() => onUpdateQuery('reset')}
                  >
                    초기화
                  </Button>
                </div>
                {/* 검색/초기화 버튼 끝 */}
              </Content>
              {/* 검색필터 끝 */}

              {/* 검색결과 */}
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 20,
                    justifyContent: 'space-between',
                  }}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Flex vertical>
                      <h4
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          marginRight: 20,
                        }}
                      >
                        검색 결과 총{' '}
                        <span style={{ color: '#0066FF', fontWeight: 700 }}>
                          {tableData?.total}
                        </span>
                        명
                      </h4>

                      <p>
                        선택된 회원{' '}
                        <span style={{ color: '#0066FF' }}>
                          {selected?.length}
                        </span>
                        명
                      </p>
                    </Flex>
                    <Flex gap={8}>
                      <Button
                        onClick={() => {
                          onSelect('all', tableData?.data);
                        }}
                      >
                        검색회원 전체
                        {selected?.length === tableData?.total
                          ? ' 해제'
                          : ' 선택'}
                      </Button>

                      <Button
                        onClick={() =>
                          navigate('/message/sms-manager', {
                            state: { users: selected ?? null },
                          })
                        }
                      >
                        SMS 전송
                      </Button>

                      <Button
                        onClick={() =>
                          navigate('/message/push-manager', {
                            state: { users: selected ?? null },
                          })
                        }
                      >
                        푸시알림 전송
                      </Button>

                      {/* 엑셀 다운로드 */}
                      <Button
                        onClick={() =>
                          downloadXlsx({
                            columns: col,
                            rows: tableData.total_data,
                            fileName: '회원대시보드',
                          })
                        }
                      >
                        엑셀로 다운로드
                      </Button>

                      {/* 회원생성 */}
                      <Button onClick={() => setIsCreate(!isCreate)}>
                        회원생성
                      </Button>

                      {/* 쿠폰 발급 */}
                      <Button
                        onClick={() => {
                          if (!selected.length) {
                            MessageAlert.info(
                              '쿠폰을 발급할 회원을 선택해주세요!',
                            );
                            return;
                          }

                          setIsIssueModal(!isIssueModal);
                        }}
                      >
                        쿠폰발급
                      </Button>

                      {/* 계정 상태 변경 */}
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 1,
                              label: '정상',
                              onClick: () => onBulk('account_status', 'ACTIVE'),
                            },
                            {
                              key: 2,
                              label: '비활성',
                              onClick: () =>
                                onBulk('account_status', 'INACTIVE'),
                            },
                            {
                              key: 3,
                              label: '휴면',
                              onClick: () =>
                                onBulk('account_status', 'DORMANT'),
                            },
                            {
                              key: 4,
                              label: '탈퇴',
                              onClick: () =>
                                onBulk('account_status', 'WITHDRAWN'),
                            },
                          ],
                        }}
                      >
                        <Button>
                          <Space>
                            계정상태
                            <DownOutlined />
                          </Space>
                        </Button>
                      </Dropdown>

                      {/* 적립금 지급 */}
                      <Button
                        onClick={() => {
                          if (!selected?.length) {
                            MessageAlert.info(
                              '적립금을 지급할 회원을 선택해주세요!',
                            );
                            return;
                          }
                          setModalType('MULTI-EARNED');
                        }}
                      >
                        적립금 지급
                      </Button>

                      {/* 등급 변경 */}
                      <Dropdown
                        menu={{
                          items: grades?.map((_item) => ({
                            key: _item?.grade_id,
                            label: _item?.grade_name,
                            onClick: () => {
                              if (!selected?.length) {
                                MessageAlert.info('회원을 선택해주세요!');
                                return;
                              }

                              setInputGrade((prev) => ({
                                ...prev,
                                grade_id: _item?.grade_id,
                                grade_name: _item?.grade_name,
                              }));
                            },
                          })),
                        }}
                      >
                        <Button>
                          <Space>
                            등급변경
                            <DownOutlined />
                          </Space>
                        </Button>
                      </Dropdown>

                      {/* 특별 관리 대상 */}
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 1,
                              label: '관리대상 지정',
                              onClick: () => onBulk('is_caution', 'Y'),
                            },
                            {
                              key: 2,
                              label: '관리대상 해제',
                              onClick: () => onBulk('is_caution', 'N'),
                            },
                          ],
                        }}
                      >
                        <Button>
                          <Space>
                            관리대상설정
                            <DownOutlined />
                          </Space>
                        </Button>
                      </Dropdown>

                      {/* 엑셀 다운로드 */}
                      <Button onClick={() => onXlsxDownload()}>
                        엑셀 다운로드
                      </Button>
                    </Flex>
                  </Flex>

                  <Flex gap={8}>
                    <Select
                      value={Number(count)}
                      options={[
                        { label: '10 / page', value: 10 },
                        { label: '30 / page', value: 30 },
                        { label: '50 / page', value: 50 },
                        { label: '100 / page', value: 100 },
                      ]}
                      onChange={(e) => onUpdateQuery('count', e)}
                    />
                  </Flex>
                </div>

                <Table
                  scroll={{
                    x: 'max-content',
                  }}
                  dataSource={tableData?.data}
                  // pagination={false}
                  defaultPageSize={50}
                  columns={col}
                  onRow={(data) => {
                    return {
                      onClick: () => onDetailModal(data?.userId),
                    };
                  }}
                  rowSelection={{
                    type: 'checkbox',
                    onSelect: (record, selected, selectedRows, nativeEvent) => {
                      onSelect(selected ? 'checked' : 'unchecked', record);
                    },
                    onSelectAll: (selected, selectedRows, changeRows) => {
                      onSelect(
                        selected ? 'all-checked' : 'all-unchecked',
                        selectedRows,
                      );
                    },
                    selectedRowKeys: selected?.map((user) => user?.key),
                  }}
                  pagination={{
                    total: tableData?.total,
                    current: page,
                    pageSize: count,
                    onChange: (p) => onUpdateQuery('page', p),
                    align: 'center',
                    style: {
                      marginTop: 20,
                      display: 'flex',
                      justifyContent: 'center',
                    },
                    showSizeChanger: false,
                  }}
                />
              </Content>
              {/* 검색결과 끝 */}
            </Col>
          </Row>
        </Content>
      </Spin>

      {/* SECTION: 회원상세모달 */}
      <ModalLayout
        type={'drawer'}
        open={modal && modalData}
        onCancel={() => {
          refetch();

          onDetailModal(null);
        }}
        title={'회원 상세 정보'}
        width={'100%'}
        maxWidth={870}
        extra={
          <>
            <Button
              size="small"
              danger
              onClick={() => onDeleteUser(modalData?.user_id)}
            >
              탈퇴
            </Button>
          </>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          {/* FIXME: 탭 추가 */}
          {/* 
          1. 탭 컴포넌트 추가
          2. 탭 구분
          3. 탭에 맞는 컨텐츠 보이기
          4. 두번째 탭 컨텐츠는 주문정보
          */}
          {/* <Segmented
            
            block
            value={detailTab}
            onChange={setDetailTab}
            options={[
              {
                label: '기본정보',
                value: 'default',
              },
              {
                label: '주문정보',
                value: 'order',
              },
            ]}
          />
          <br />
          <br /> */}

          {/* === 모달 > 상세보기: start === */}
          {detailTab === 'default' ? (
            <Row>
              {/* === 모달 > 상세보기: end === */}
              <Col x={24}>
                <Content maxWidth={'100%'} padding={'0px 0px 20px'}>
                  <Title size={5} style={{ marginBottom: 5 }}>
                    회원 주문 정보
                  </Title>
                  {modalData ? (
                    <AntDescriptions
                      bordered
                      column={2}
                      size="small"
                      labelStyle={styles.labelStyle}
                      contentStyle={styles.contentStyle}
                      style={styles.defaultStyle}
                      items={[
                        {
                          key: `order1`,
                          label: 'JM Pay 잔액',
                          children: (
                            <>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <p>
                                  {stringToMoneyFormat(modalData?.jm_pay, 'P')}
                                </p>
                                <p>
                                  <Button
                                    size="small"
                                    type="link"
                                    style={{
                                      textDecoration: 'underline',
                                      padding: 0,
                                    }}
                                    onClick={() => setJmpayModal(true)}
                                  >
                                    상세보기
                                  </Button>
                                </p>
                              </div>
                            </>
                          ),
                        },
                        {
                          key: `order2`,
                          label: '보유 포인트',
                          children: (
                            <Flex align="center" justify="space-between">
                              <Text size={14}>
                                {stringToMoneyFormat(
                                  modalData?.totalPoint,
                                  'P',
                                )}
                              </Text>

                              <Flex gap={4}>
                                <Button
                                  danger
                                  size="small"
                                  onClick={() => setModalType('DEDUCTED')}
                                >
                                  차감
                                </Button>
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={() => setModalType('EARNED')}
                                >
                                  적립
                                </Button>
                              </Flex>
                            </Flex>
                          ),
                        },
                        {
                          key: `order3`,
                          label: '보유 스탬프',
                          children: (
                            <button
                              style={{
                                border: 'none',
                                background: 'none',
                                color: '#1C6BFF',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                navigate(`/users/stamp_history`, {
                                  state: { user_phone: modalData?.user_phone },
                                });
                              }}
                            >
                              {stringToMoneyFormat(
                                modalData?.stampHistory?.reduce(
                                  (arr, stamp) => (arr += stamp.stamp_num),
                                  0,
                                ),
                                '개',
                              )}
                            </button>
                          ),
                        },
                        {
                          key: `order4`,
                          label: '오더 주문',
                          children: (
                            <button
                              style={{
                                border: 'none',
                                background: 'none',
                                color: '#1C6BFF',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setIsOpened(!isOpened);
                                setModalType('ORDER');
                              }}
                            >
                              {stringToMoneyFormat(
                                modalData?.orderHistory?.length,
                                '건',
                              )}
                            </button>
                          ),
                        },
                        {
                          key: `order5`,
                          label: '오더 총 결제금액',
                          children: stringToMoneyFormat(
                            modalData?.totalOrderPrice,
                            '원',
                          ),
                        },
                        {
                          key: `order6`,
                          label: '몰 주문',
                          children: (
                            <button
                              style={{
                                border: 'none',
                                background: 'none',
                                color: '#1C6BFF',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setIsOpened(!isOpened);
                                setModalType('SHOP_ORDER');
                              }}
                            >
                              {stringToMoneyFormat(
                                modalData?.shopOrderHistory?.length,
                                '건',
                              )}
                            </button>
                          ),
                        },

                        {
                          key: `order7`,
                          label: '몰 총 결제금액',
                          children: stringToMoneyFormat(
                            modalData?.totalShopOrderPice,
                            '원',
                          ),
                        },
                      ]}
                    />
                  ) : null}
                </Content>

                {/* === 회원정보: start === */}
                <Title size={5} style={{ marginBottom: 5 }}>
                  회원 정보
                </Title>

                {modalData ? (
                  <AntDescriptions
                    bordered
                    column={2}
                    size="small"
                    labelStyle={styles.labelStyle}
                    contentStyle={styles.contentStyle}
                    style={styles.defaultStyle}
                    items={[
                      {
                        key: '1',
                        label: '회원번호',
                        children: `${modalData?.user_id}`,
                      },
                      {
                        key: '2',
                        label: '회원명',
                        children: `${modalData?.user_name ?? '-'}`,
                      },
                      {
                        key: '3',
                        label: '회원 아이디',
                        children: `${(modalData?.user_account ?? '-')?.replace(
                          /\b(\d{10})\b/g, // 10자리 숫자 패턴 (유닉스 타임스탬프)
                          (match) => {
                            return dayjs
                              .unix(match)
                              .format('YYYY-MM-DD HH:mm:ss');
                          },
                        )}`,
                      },
                      {
                        key: '4',
                        label: '계정상태',
                        children: (
                          <Select
                            style={{ width: '100%' }}
                            value={modalData?.account_status}
                            options={Object.entries(ACCOUNT_STATUS)
                              .filter(([key]) => key !== 'WITHDRAWN')
                              .map(([key, value]) => ({
                                label: value?.label,
                                value: key,
                              }))}
                            onChange={(v) =>
                              changeAccountStatus('account_status', v)
                            }
                          />
                        ),
                      },
                      {
                        key: '5',
                        label: '연락처',
                        children: `${(modalData?.user_phone ?? '-')?.replace(
                          /\b(\d{10})\b/g, // 10자리 숫자 패턴 (유닉스 타임스탬프)
                          (match) => {
                            return dayjs
                              .unix(match)
                              .format('YYYY-MM-DD HH:mm:ss');
                          },
                        )}`,
                      },
                      {
                        key: '6',
                        label: '이메일',
                        children: `${modalData?.user_email ?? '-'}`,
                      },
                      {
                        key: '7',
                        label: '성별',
                        children: `${
                          modalData?.user_gender === 'M' ? '남' : '여'
                        }`,
                      },
                      {
                        key: '8',
                        label: '생년월일',
                        children: `${Formatter.unixToDate(
                          modalData?.user_birthday,
                          'YYYY.MM.DD',
                        )}`,
                      },
                      {
                        key: '18',
                        label: '회원등급',
                        children: (
                          <Select
                            value={modalData?.grade_id}
                            options={grades?.map((grade) => ({
                              label: grade?.grade_name,
                              value: grade?.grade_id,
                            }))}
                            onChange={(v) => changeAccountStatus('grade_id', v)}
                            style={{ width: '100%' }}
                          />
                        ),
                      },
                      {
                        key: '19',
                        label: '비밀번호',
                        children: (
                          <Button onClick={() => setUpdateModal('PASSWORD')}>
                            변경
                          </Button>
                        ),
                      },
                      {
                        key: '9',
                        label: '주소',
                        children: (
                          <Flex justify="space-between" align="center">
                            <Text size={14} maxLine={2}>{`(${
                              modalData?.postcode
                            }) ${modalData?.user_address ?? '-'}\n${
                              modalData?.user_address_detail ?? ''
                            }`}</Text>
                            <Button onClick={() => setUpdateModal('ADDRESS')}>
                              변경
                            </Button>
                          </Flex>
                        ),
                        span: 2,
                      },
                      {
                        key: '10',
                        label: '닉네임',
                        children: `${modalData?.user_nick_name ?? '-'}`,
                      },
                      {
                        key: '11',
                        label: '멤버십 번호',
                        children: `${
                          Formatter.membership(modalData?.membership_code) ??
                          '-'
                        }`,
                      },
                      {
                        key: '12',
                        label: '가입일',
                        children: `${Formatter.unixToDate(
                          modalData?.created_at,
                          'YYYY.MM.DD HH:mm:ss',
                        )}`,
                      },
                      {
                        key: '13',
                        label: '최근 접속일',
                        children: `${Formatter.unixToDate(
                          modalData?.access_time,
                          'YYYY.MM.DD HH:mm:ss',
                        )}`,
                      },
                      {
                        key: '14',
                        label: '가입경로',
                        children: `${
                          modalData?.social_type
                            ? SIGNUP_PATH[modalData?.social_type]?.label
                            : modalData?.user_name
                              ? SIGNUP_PATH['MEMBER']?.label
                              : SIGNUP_PATH['NONE']?.label
                        }`,
                      },
                      {
                        key: '15',
                        label: '보유쿠폰',
                        children: (
                          <p>
                            <button
                              style={{
                                border: 'none',
                                background: 'none',
                                color: '#1C6BFF',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setIsOpened(!isOpened);
                                setModalType('COUPON');
                              }}
                            >
                              {stringToMoneyFormat(
                                modalData?.couponStorage?.useable?.length,
                                '개',
                              )}
                            </button>
                          </p>
                        ),
                      },
                      {
                        key: '16',
                        label: '회원설정',
                        children: (
                          <div>
                            {Object.entries(modalData?.user_setting || {}) // user_setting이 없으면 빈 객체 처리
                              .map(([key, value], index) => {
                                if (!USER_SETTING[key]) return null; // 조건이 충족되지 않으면 null 반환

                                return (
                                  <p
                                    style={{
                                      // margin: '8px 0px',
                                      margin: 0,
                                      padding: '5px 0',
                                      textAlign: 'left',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      borderTop:
                                        index > 0
                                          ? '1px solid #f1f1f1'
                                          : 'none',
                                    }}
                                  >
                                    <p style={{ margin: 0 }}>
                                      {USER_SETTING[key]}:
                                    </p>{' '}
                                    <p style={{ margin: 0 }}>
                                      {value ? (
                                        <Tag
                                          icon={<CheckCircleOutlined />}
                                          color="success"
                                        >
                                          동의
                                        </Tag>
                                      ) : (
                                        <Tag
                                          icon={<MinusCircleOutlined />}
                                          color="default"
                                        >
                                          미동의
                                        </Tag>
                                      )}
                                    </p>
                                    {/* <p>{value ? '동의' : '미동의'}</p> */}
                                  </p>
                                );
                              })}
                          </div>
                        ),
                        span: 2,
                      },
                      {
                        key: '17',
                        label: (
                          <p>
                            메모{' '}
                            <Button
                              size="small"
                              style={{
                                // border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                padding: '0 6px',
                                marginLeft: 3,
                                height: 19,
                              }}
                              onClick={() => {
                                setIsOpened(!isOpened);
                                setModalType('MEMO');
                              }}
                            >
                              <EditOutlined style={{ fontSize: '0.85em' }} />
                            </Button>
                          </p>
                        ),
                        children: (
                          <p
                            style={{
                              whiteSpace: 'pre-line',
                              textAlign: 'left',
                            }}
                          >
                            {modalData?.memo}
                          </p>
                        ),
                        span: 2,
                      },
                    ]}
                  />
                ) : null}

                {/* === 사업자정보: start === */}
                {modalData?.business_level !== 0 ? (
                  <Content
                    maxWidth={'100%'}
                    padding={0}
                    style={{ marginTop: 20 }}
                  >
                    <Title size={5} style={{ marginBottom: 5 }}>
                      사업자 정보
                    </Title>

                    <AntDescriptions
                      bordered
                      column={2}
                      size="small"
                      labelStyle={styles.labelStyle}
                      contentStyle={styles.contentStyle}
                      style={styles.defaultStyle}
                      items={[
                        {
                          key: '1',
                          label: '사업자번호',
                          children: `${modalData?.business_num}`,
                        },
                        {
                          key: '2',
                          label: '승인상태',
                          children: (
                            <Flex justify="space-between" align="center">
                              <Tag
                                color={
                                  modalData?.business_level === 1
                                    ? 'processing'
                                    : 'success'
                                }
                              >
                                {modalData?.business_level === 1
                                  ? '심사중'
                                  : '승인'}
                              </Tag>

                              {modalData?.business_level === 1 ? (
                                <Button
                                  size="small"
                                  icon={<CheckCircleOutlined />}
                                  style={{ fontSize: 12 }}
                                  onClick={() =>
                                    onApprove(modalData, fetchModalData)
                                  }
                                >
                                  승인
                                </Button>
                              ) : null}
                            </Flex>
                          ),
                        },
                        {
                          key: '3',
                          label: '사업자등록증',
                          children: (
                            <Image
                              src={`${STORE_URL}/${modalData?.business_num_url}`}
                              height={150}
                              alt="사업자등록증"
                            />
                          ),
                          span: 2,
                        },
                      ]}
                    />
                  </Content>
                ) : null}
              </Col>
              {/* === 회원정보: end === */}
            </Row>
          ) : (
            <div>
              {/* === 모달 > 상세보기 > 주문내역: end === */}
              {/* 
              1. 주문정보 조회
              2. 테이블
              */}
            </div>
          )}
        </Content>
      </ModalLayout>

      {/* SECTION: 메모 모달 */}
      <ModalLayout
        type={'modal'}
        open={isOpened && modalType === 'MEMO'}
        onCancel={() => {
          setIsOpened(!isOpened);
          setModalType('');
        }}
        title={'회원 메모'}
        width={500}
        footer={
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Button onClick={() => setIsOpened(!isOpened)}>닫기</Button>
            <Button
              type="primary"
              onClick={() => {
                onSubmit(
                  'account_status',
                  modalData?.user_id,
                  { memo: memo },
                  fetchModalData,
                );
              }}
            >
              저장
            </Button>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            <Col x={24}>
              <Input.TextArea
                style={{ width: '100%', height: 300 }}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: 오더 주문 내역 모달 */}
      <ModalLayout
        type={'modal'}
        open={isOpened && (modalType === 'ORDER' || modalType === 'SHOP_ORDER')}
        onCancel={() => {
          setIsOpened(!isOpened);
          setModalType('');
        }}
        title={'회원 주문내역'}
        closable
        centered
      >
        <Divider style={{ margin: '13px 0' }} />
        <OrderHistory
          keyType={modalType.toLocaleLowerCase()}
          data={
            modalType === 'ORDER'
              ? modalData?.orderHistory
              : modalData?.shopOrderHistory
          }
        />
      </ModalLayout>

      {/* SECTION: 쿠폰 모달 */}
      <ModalLayout
        type={'modal'}
        open={isOpened && modalType === 'COUPON'}
        onCancel={() => {
          setIsOpened(!isOpened);
          setModalType('');
        }}
        closable
        title={'보유쿠폰'}
        width={560}
        centered
      >
        <Divider style={{ margin: '13px 0' }} />
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            <Col x={24}>
              <ScrollLayout
                style={{
                  width: '100%',
                  maxHeight: document.body.clientHeight * 0.5,
                  height: document.body.clientHeight * 0.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                }}
              >
                {modalData?.couponStorage?.useable?.map((coupon, index) => (
                  <div
                    key={`coupon-${coupon?.coupon_user_id}-${index}`}
                    style={styles.couponLayout}
                  >
                    <div style={{ width: '70%' }}>
                      <div style={styles.couponTitle}>
                        {coupon?.policy_coupon?.coupon_name}
                      </div>
                      <div style={styles.couponStatus}>
                        {coupon?.policy_coupon?.coupon_content?.benefit}
                      </div>
                      <div style={styles.couponDate}>
                        사용기한 : ~{' '}
                        {Formatter.unixToDate(
                          coupon?.expired_date,
                          'YYYY.MM.DD HH:mm',
                        )}
                      </div>
                    </div>

                    <button
                      style={{
                        border: '1px solid #111111',
                        background: '#FFFFFF',
                        fontSize: 14,
                        position: 'absolute',
                        top: '50%',
                        right: 20,
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        padding: '8px 16px',
                      }}
                      onClick={() => onUsedCoupon(coupon?.coupon_user_id)}
                    >
                      사용처리
                    </button>
                  </div>
                ))}
              </ScrollLayout>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: 회원생성 모달 */}
      <ModalLayout
        type={'drawer'}
        open={isCreate}
        onCancel={() => setIsCreate(!isCreate)}
        title={'회원 생성'}
        width={'100%'}
        maxWidth={870}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <UserRegister onClose={() => setIsCreate(!isCreate)} />
      </ModalLayout>

      {/* SECTION: 회원 수정 모달 */}
      <ModalLayout
        type={'modal'}
        open={!!updateModal}
        onCancel={() => setUpdateModal(null)}
        closable
        centered
        width={500}
        title={
          updateModal === 'PASSWORD'
            ? '패스워드 변경'
            : updateModal === 'ADDRESS'
              ? '주소 변경'
              : null
        }
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Flex gap={10} justify="flex-end">
              <Button onClick={() => setUpdateModal(null)}>취소</Button>
              <Button
                type="primary"
                onClick={() => {
                  if (
                    !window.confirm(
                      `회원의 ${
                        updateModal === 'PASSWORD'
                          ? '패스워드'
                          : updateModal === 'ADDRESS'
                            ? '주소'
                            : null
                      }를 변경하시겠습니까?`,
                    )
                  )
                    return;

                  onSubmit(
                    'account_status',
                    modalData?.user_id,
                    inputValue,
                    fetchModalData,
                  );
                  setInputValue({});
                }}
              >
                수정
              </Button>
            </Flex>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            <Col x={24}>
              {updateModal === 'PASSWORD' ? (
                <Input.Password
                  value={inputValue?.password}
                  size="large"
                  onChange={(e) =>
                    setInputValue((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  placeholder="변경할 비밀번호를 입력해주세요"
                />
              ) : updateModal === 'ADDRESS' ? (
                <Flex gap={4} vertical>
                  <Flex gap={4}>
                    <Input
                      size="large"
                      value={inputValue?.postcode}
                      placeholder="우편번호"
                    />
                    <Button
                      size="large"
                      onClick={() =>
                        openAddressModal((res) =>
                          setInputValue((prev) => ({
                            ...prev,
                            postcode: res.zonecode,
                            user_address: res.address,
                          })),
                        )
                      }
                    >
                      주소검색
                    </Button>
                  </Flex>
                  <Input
                    size="large"
                    value={inputValue?.user_address}
                    placeholder="주소"
                  />
                  <Input
                    size="large"
                    value={inputValue?.user_address_detail}
                    placeholder="주소 상세"
                    onChange={(e) =>
                      setInputValue((prev) => ({
                        ...prev,
                        user_address_detail: e.target.value,
                      }))
                    }
                  />
                </Flex>
              ) : null}
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: 적립금 지금/차감 모달 */}
      <ModalLayout
        type={'modal'}
        open={
          modalType &&
          (modalType === 'EARNED' ||
            modalType === 'DEDUCTED' ||
            modalType === 'MULTI-EARNED')
        }
        onCancel={() => setUpdateModal(null)}
        centered
        width={500}
        title={`적립금 ${modalType?.includes('EARNED') ? '지급' : '차감'}`}
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Flex gap={10} justify="flex-end">
              <Button onClick={() => setModalType('')}>취소</Button>
              <Button
                type={'primary'}
                danger={modalType?.includes('EARNED') ? false : true}
                onClick={async () => {
                  const res = await onReservePoint(
                    modalType,
                    {
                      ...(modalType?.includes('MULTI')
                        ? {
                            users: selected,
                          }
                        : { user_id: modalData?.user_id }),
                      ...inputReserveValue,
                    },
                    setModalData,
                  );

                  if (res) {
                    setModalType('');
                    setInputReserveValue({});
                  }
                }}
              >
                {modalType?.includes('EARNED') ? '지급' : '차감'}
              </Button>
            </Flex>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            <Col x={24}>
              <Flex vertical gap={4}>
                <InputNumber
                  value={inputReserveValue?.point ?? 0}
                  onChange={(e) =>
                    setInputReserveValue((prev) => ({
                      ...prev,
                      point: e,
                    }))
                  }
                  placeholder={`${
                    modalType === 'EARNED' ? '지급' : '차감'
                  }포인트`}
                  min={0}
                  style={{ width: '100%' }}
                  size="large"
                  suffix={'P'}
                />
                {modalType?.includes('EARNED') ? (
                  <InputNumber
                    value={inputReserveValue?.expired_date}
                    onChange={(e) =>
                      setInputReserveValue((prev) => ({
                        ...prev,
                        expired_date: e,
                      }))
                    }
                    placeholder={`만료일 (지급일 기준)`}
                    min={0}
                    style={{ width: '100%' }}
                    size="large"
                    suffix={'일'}
                    prefix={'지급일로부터'}
                  />
                ) : null}

                <Input.TextArea
                  value={inputReserveValue?.memo}
                  onChange={(e) =>
                    setInputReserveValue((prev) => ({
                      ...prev,
                      memo: e.target.value,
                    }))
                  }
                  placeholder={`${
                    modalType?.includes('EARNED') ? '적립' : '차감'
                  }사유를 입력해주세요.`}
                  maxLength={100}
                  style={{ height: 120 }}
                />
              </Flex>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: JM페이 내역 모달 */}
      <ModalLayout
        type={'modal'}
        open={jmpayModal}
        onCancel={() => setJmpayModal(!jmpayModal)}
        closable
        centered
        width={500}
        title={`JM페이 내역`}
      >
        <Content
          maxWidth={'100%'}
          padding={0}
          maxHeight={'80vh'}
          style={{ overflowY: 'auto' }}
        >
          <Row>
            <Col x={24}>
              <Flex vertical gap={8}>
                {modalData?.jmpay?.length ? (
                  modalData?.jmpay?.map((_item, _index) => (
                    <Flex
                      key={`jm_history_${_item?.jm_pay_id}_${_index}`}
                      style={{
                        padding: 10,
                        border: '1px solid #F5F5F5',
                        borderRadius: 5,
                      }}
                      vertical
                    >
                      <Flex justify="space-between" align="center" flex={1}>
                        <Flex>
                          <Tag color={JM_PAY[_item?.type]?.color}>
                            {JM_PAY[_item?.type]?.label}
                          </Tag>

                          <Text size={14} weight={700}>
                            {stringToMoneyFormat(_item?.price, '원')}
                          </Text>
                        </Flex>

                        {_item?.type?.includes('CHARGE') ||
                        _item?.type === 'CANCELLED' ? (
                          <Text size={14} color={'#BDBDBD'}>
                            {_item?.type === 'CANCELLED' ? '취소' : '충전'} 후
                            잔액:{' '}
                            {stringToMoneyFormat(_item?.current_balance, '원')}
                          </Text>
                        ) : null}
                      </Flex>

                      <Flex flex={1} gap={4} style={{ marginTop: 8 }}>
                        <Text size={14} color={'#BDBDBD'}>
                          {dayjs
                            .unix(_item?.created_at)
                            .format('YYYY.MM.DD HH:mm:ss')}
                        </Text>
                      </Flex>
                    </Flex>
                  ))
                ) : (
                  <Empty />
                )}
              </Flex>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: 등급 변경 모달 */}
      <ModalLayout
        type={'modal'}
        open={!!inputGrade?.grade_id}
        onCancel={() => setInputGrade({ grade_id: null, grade_reason: '' })}
        centered
        width={500}
        title={`등급변경`}
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Flex gap={10} justify="flex-end">
              <Button onClick={() => setModalType('')}>취소</Button>
              <Button
                type="primary"
                onClick={async () => {
                  const res = await onUpdateGrade(inputGrade);

                  if (res) {
                    setInputGrade({
                      grade_id: null,
                      grade_reason: '',
                      grade_name: '',
                    });
                  }
                }}
              >
                변경
              </Button>
            </Flex>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={0}>
          <Row>
            <Col x={24}>
              <Flex vertical gap={4}>
                <Input.TextArea
                  value={inputGrade?.grade_reason}
                  onChange={(e) =>
                    setInputGrade((prev) => ({
                      ...prev,
                      grade_reason: e.target.value,
                    }))
                  }
                  placeholder={`등급 변경 사유를 입력해주세요.`}
                  maxLength={100}
                  style={{ height: 120 }}
                />
              </Flex>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* SECTION: 쿠폰발급 */}
      <Modal
        visible={visible}
        title={'발급수량을 입력해주세요.'}
        centered
        okText={'발급'}
        cancelText={'취소'}
        onOk={() =>
          onIssueCoupon(selectedCoupon, issuedNumb, () => {
            setIsIssueModal(!isIssueModal);
            setSelectedCoupon(null);
            setVisible(!visible);
          })
        }
        onCancel={() => setVisible(!visible)}
        zIndex={5000}
        afterClose={() => setIssuedNumb(1)}
      >
        <InputNumber
          value={issuedNumb}
          placeholder="발급수량을 입력해주세요."
          onChange={setIssuedNumb}
          min={1}
          style={{ width: '100%' }}
        />
      </Modal>
      <ModalLayout
        type={'modal'}
        open={isIssueModal}
        onCancel={() => {
          setIsIssueModal(!isIssueModal);
          setSelectedCoupon(null);
        }}
        closable
        title={'쿠폰 발급'}
        centered
        footer={
          <Flex gap={4} justify="flex-end">
            <Button
              onClick={() => {
                setIsIssueModal(!isIssueModal);
                setSelectedCoupon(null);
              }}
            >
              취소
            </Button>
            <Button
              type="primary"
              disabled={!selectedCoupon}
              onClick={() => setVisible(!visible)}
            >
              발급
            </Button>
          </Flex>
        }
      >
        <Spin spinning={isLoading}>
          <Content maxWidth={'100%'} padding={0}>
            <Text color={'#D33444'} size={12}>
              쿠폰발급은 수동발급 쿠폰 또는 다운로드 쿠폰만 발급 가능합니다.
            </Text>
            <Table
              dataSource={coupon}
              columns={[
                {
                  key: 'coupon_name',
                  title: '쿠폰명',
                  dataIndex: 'coupon_name',
                  ...getColumnSearchProps('coupon_name'),
                },
                {
                  key: 'coupon_type',
                  title: '타입',
                  dataIndex: 'coupon_type',
                  render: (item) => (
                    <Tag color={COUPON_TYPE[item]?.color}>
                      {COUPON_TYPE[item]?.label}
                    </Tag>
                  ),
                  filters: Object.entries(COUPON_TYPE).map(([key, value]) => ({
                    text: value?.label,
                    value: key,
                  })),
                  onFilter: (value, record) => record?.coupon_type === value,
                },
                {
                  key: 'voucher_type',
                  title: '할인타입',
                  dataIndex: 'voucher_type',
                  render: (item) => (
                    <Tag color={VOUCHER_TYPE[item]?.color}>
                      {VOUCHER_TYPE[item]?.label}
                    </Tag>
                  ),
                  filters: Object.entries(VOUCHER_TYPE).map(([key, value]) => ({
                    text: value?.label,
                    value: key,
                  })),
                  onFilter: (value, record) => record?.voucher_type === value,
                },
                {
                  key: 'discount',
                  title: '할인',
                  dataIndex: 'product_policy',
                  render: (item) => {
                    const { discount_rate, discount_type } = item;

                    return stringToMoneyFormat(
                      discount_rate,
                      discount_type === 'W' ? '원' : '%',
                    );
                  },
                },
                {
                  key: 'max_discount',
                  title: '최대 할인',
                  dataIndex: 'product_policy',
                  render: ({ max_discount_price }) => {
                    return stringToMoneyFormat(max_discount_price, '원');
                  },
                },
                {
                  key: 'minimum_price',
                  title: '최소사용금액',
                  dataIndex: 'minimum_price',
                  render: (item) => {
                    return stringToMoneyFormat(item, '원');
                  },
                },
              ]}
              rowSelection={{
                type: 'radio',
                onChange: (e) => {
                  setSelectedCoupon(e?.[0]);
                },
                selectedRowKeys: [selectedCoupon],
              }}
            />
          </Content>
        </Spin>
      </ModalLayout>
    </React.Fragment>
  );
};

export default UserDashboardPresenter;
